import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMap } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import LocationIcon from '@/assets/icons/location-icon.svg';
import LocationErrorIcon from '@/assets/icons/location-error-icon.svg';
import { normalizeKey } from '@/helpers/format/normalizeStrings';
import { Palette } from '@/themes/themeOverrides/Palette';

const markerKey = collectionPoint => {
    if (collectionPoint?.id != null && collectionPoint.id !== '') return String(collectionPoint.id);
    return `fallback:${collectionPoint?.customId ?? ''}:${collectionPoint?.latitude ?? ''}:${collectionPoint?.longitude ?? ''}`;
};

export const createCollectionPointMarkerContent = collectionPoint => {
    const markerElement = document.createElement('div');
    markerElement.style.position = 'relative';

    const hasOfflineDevice = collectionPoint.devices && collectionPoint.devices.some(device => device.state === 'offline' || device.state === null);
    const iconIconHtml = hasOfflineDevice
        ? `<img src="${LocationErrorIcon}" width="16" height="16" alt="" />`
        : `<img src="${LocationIcon}" width="16" height="16" alt="" />`;

    markerElement.innerHTML = `
        <div style="position: relative; display: flex; align-items: center; justify-content: center;">
            <div style="
                width: 32px;
                height: 32px;
                background-color: #FFF;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                position: relative;
            ">
                ${iconIconHtml}
            </div>
            <div style="
                position: absolute;
                bottom: -5px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 6px solid transparent;
                border-right: 6px solid transparent;
                border-top: 6px solid #FFF;
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
            "></div>
        </div>
    `;

    return markerElement;
};

const clusterRenderer = {
    render: ({ count, position }) => {
        const clusterElement = document.createElement('div');
        clusterElement.style.cssText = `
            width: 43px;
            height: 28px;
            background-color: #FFF;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            font-size: 14px;
            font-weight: 600;
            color: ${Palette.black};
        `;
        clusterElement.textContent = String(count);

        return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: clusterElement,
            title: String(count),
            zIndex: count,
        });
    },
};

export const ClusteredCollectionPointMarkers = ({ collectionPointData, onMarkerClick, onMarkersReady }) => {
    const map = useMap();
    const clustererRef = useRef(null);
    const markersByIdRef = useRef(new Map());
    const customIdToMarkerRef = useRef(new Map());
    const onMarkerClickRef = useRef(onMarkerClick);
    const onMarkersReadyRef = useRef(onMarkersReady);

    useEffect(() => {
        onMarkerClickRef.current = onMarkerClick;
    }, [onMarkerClick]);

    useEffect(() => {
        onMarkersReadyRef.current = onMarkersReady;
    }, [onMarkersReady]);

    useEffect(() => {
        if (!map || !collectionPointData?.length) return;

        if (!clustererRef.current) {
            clustererRef.current = new MarkerClusterer({
                map,
                markers: [],
                algorithmOptions: { radius: 80 },
                renderer: clusterRenderer,
            });
            onMarkersReadyRef.current?.({
                collectionPointIdToMarker: markersByIdRef.current,
                customIdToMarker: customIdToMarkerRef.current,
            });
        }

        const newMarkers = [];

        collectionPointData.forEach(collectionPoint => {
            if (!Number.isFinite(collectionPoint?.latitude) || !Number.isFinite(collectionPoint?.longitude)) return;

            const key = markerKey(collectionPoint);
            if (markersByIdRef.current.has(key)) return;

            const marker = new google.maps.marker.AdvancedMarkerElement({
                map,
                position: { lat: collectionPoint.latitude, lng: collectionPoint.longitude },
                title: `${collectionPoint?.customId ?? ''} ${collectionPoint?.shopName ?? ''}`.trim(),
                content: createCollectionPointMarkerContent(collectionPoint),
            });

            marker.addListener('gmp-click', () => onMarkerClickRef.current?.(collectionPoint, marker));

            markersByIdRef.current.set(key, marker);
            if (collectionPoint?.customId) {
                customIdToMarkerRef.current.set(normalizeKey(String(collectionPoint.customId)), marker);
            }
            newMarkers.push(marker);
        });

        if (newMarkers.length > 0) {
            clustererRef.current.addMarkers(newMarkers);
        }
    }, [map, collectionPointData]);

    useEffect(() => {
        const markersById = markersByIdRef.current;
        const customIdToMarker = customIdToMarkerRef.current;

        return () => {
            clustererRef.current?.clearMarkers();
            markersById.forEach(marker => {
                marker.map = null;
            });
            markersById.clear();
            customIdToMarker.clear();
            clustererRef.current = null;
        };
    }, [map]);

    return null;
};

ClusteredCollectionPointMarkers.propTypes = {
    collectionPointData: PropTypes.arrayOf(PropTypes.object),
    onMarkerClick: PropTypes.func,
    onMarkersReady: PropTypes.func,
};
